<template>
  <FormGrouping>
    <h4 :class="title">Monthly</h4>
    <FormGroup label="New inboxes">
      <PlanLimitValue :value="subscription.monthlyInboxes" />
    </FormGroup>
    <FormGroup label="Outbound emails">
      <PlanLimitValue :value="subscription.monthlySend" />
    </FormGroup>
    <FormGroup label="Inbound emails">
      <PlanLimitValue :value="subscription.monthlyReceive" />
    </FormGroup>
    <h4 :class="title">Total</h4>
    <FormGroup label="Domains">
      <PlanLimitValue :value="subscription.domains" />
    </FormGroup>
    <FormGroup label="Users">
      <PlanLimitValue :value="userLimits.members || subscription.members" />
    </FormGroup>
    <FormGroup label="Teams">
      <PlanLimitValue :value="subscription.organizations || -1" />
    </FormGroup>
    <h4 :class="title">Features</h4>
    <FormGroup label="Attachments">
      <PlanLimitValueBoolean :value="subscription.attachments" />
    </FormGroup>
    <FormGroup label="Bulk action">
      <PlanLimitValueBoolean :value="subscription.bulk" />
    </FormGroup>
    <FormGroup label="Permanent inbox">
      <PlanLimitValueBoolean :value="subscription.permanentInbox" />
    </FormGroup>
    <FormGroup label="SAML SSO">
      <PlanLimitValueBoolean :value="subscription.samlSSO || false" />
    </FormGroup>
    <FormGroup label="Webhooks">
      <PlanLimitValueBoolean :value="subscription.webhooks" />
    </FormGroup>
  </FormGrouping>
</template>
<script lang="ts" setup>
import PlanLimitValue from './PlanLimitValue.vue';
import FormGroup from './FormGroup.vue';
import type { PlanLimits, SubscriptionDto } from '@mailslurp/admin-sdk';
import FormGrouping from './FormGrouping.vue';
import PlanLimitValueBoolean from './PlanLimitValueBoolean.vue';
import { computed } from 'vue';

import { assertUnreachable } from '../../lib/services/assertUnreachable';

const props = defineProps<{
  subscription: SubscriptionDto;
  userLimits: PlanLimits;
}>();
const title = 'font-semibold';

const items = computed(() => {
  return Object.keys(props.userLimits).map((key) => {
    return getItem(key as keyof PlanLimits);
  });
});

function getItem(key: keyof PlanLimits): {
  label: string;
  value: string | number;
} {
  switch (key) {
    case 'maxUniqueRecipients': {
      return {
        label: 'Max unique recipients',
        value: props.userLimits.maxUniqueRecipients,
      };
    }
    case 'members': {
      return {
        label: 'Members',
        value: props.userLimits.members,
      };
    }
    case 'monthlyInboxes': {
      return {
        label: 'Monthly inboxes',
        value: props.userLimits.monthlyInboxes,
      };
    }
    case 'monthlyReceive': {
      return {
        label: 'Monthly receive',
        value: props.userLimits.monthlyReceive,
      };
    }
    case 'monthlySend': {
      return {
        label: 'Monthly send',
        value: props.subscription.monthlySend,
      };
    }
    case 'organizations': {
      return {
        label: 'Organizations',
        value: props.userLimits.organizations,
      };
    }
    case 'domains': {
      return {
        label: 'Domains',
        value: props.userLimits.domains,
      };
    }
    case 'aliases': {
      return {
        label: 'Aliases',
        value: props.userLimits.aliases,
      };
    }
    case 'phoneNumbers': {
      return {
        label: 'Phone numbers',
        value: props.userLimits.phoneNumbers,
      };
    }
    case 'monthlyGetMailServerRecords': {
      return {
        label: 'Monthly get mail server records',
        value: props.userLimits.monthlyGetMailServerRecords,
      };
    }
    case 'monthlyVerifyEmailAddress': {
      return {
        label: 'Monthly verify email address',
        value: props.userLimits.monthlyVerifyEmailAddress,
      };
    }
    case 'recipients': {
      return {
        label: 'Recipients',
        value: props.userLimits.recipients,
      };
    }
    case 'threads': {
      return {
        label: 'Threads',
        value: props.userLimits.threads,
      };
    }
    default: {
      const exhaustiveCheck: never = key;
      return assertUnreachable(exhaustiveCheck);
    }
  }
}
</script>
